import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useGift } from '../../../context/GiftContext';
import { useTCoins, TCOINS_TYPES } from '../../../context/TCoinsContext';

const GiftModal = ({ isOpen, onClose, toUserId, toUserName, context = 'PERSONAL', relatedId = null, relatedType = null }) => {
  const { currentUser } = useAuth();
  const { giveGift, GIFTS, loading: giftLoading } = useGift();
  const { getTotalTCoins, tcoins } = useTCoins();
  
  const [selectedGift, setSelectedGift] = useState(null);
  const [isGiving, setIsGiving] = useState(false);
  const [message, setMessage] = useState('');

  // จัดการ keyboard events
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedGift(null);
      setMessage('');
      setIsGiving(false);
    }
  }, [isOpen]);

  const handleGiveGift = async () => {
    if (!selectedGift) {
      alert('กรุณาเลือกของขวัญ');
      return;
    }

    // ตรวจสอบว่าไม่ใช่การให้ของขวัญตัวเอง
    if (currentUser.uid === toUserId) {
      alert('ไม่สามารถให้ของขวัญตัวเองได้!');
      return;
    }

    const totalTCoins = getTotalTCoins();
    if (totalTCoins < selectedGift.price) {
      alert('T Coins ไม่เพียงพอ!');
      return;
    }

    setIsGiving(true);

    try {
      const result = await giveGift({
        giftType: selectedGift.id,
        toUserId,
        toUserName,
        context,
        relatedId,
        relatedType,
        message: message.trim() || null
      });

      if (result.success) {
        alert(`ให้${selectedGift.name}ให้${toUserName}เรียบร้อยแล้ว! 🎁`);
        onClose();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (error) {
      console.error('Error giving gift:', error);
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setIsGiving(false);
    }
  };

  if (!isOpen) return null;

  const totalTCoins = getTotalTCoins();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">🎁 ให้ของขวัญ</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            data-modal-close="true"
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <p className="text-gray-600 mb-2">
            ให้ของขวัญ: <span className="font-semibold">{toUserName}</span>
          </p>
          <p className="text-sm text-gray-500">
            T Coins ที่มี: <span className="font-bold text-blue-600">{totalTCoins.toLocaleString()}</span>
          </p>
          {tcoins.real > 0 && (
            <p className="text-xs text-gray-400">
              Real Coins: {tcoins.real.toLocaleString()} | Virtual Coins: {tcoins.virtual.toLocaleString()}
            </p>
          )}
        </div>

        <div className="space-y-3 mb-6">
          {GIFTS.map((gift) => {
            const canAfford = totalTCoins >= gift.price;
            return (
              <div
                key={gift.id}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedGift?.id === gift.id
                    ? 'border-pink-500 bg-pink-50'
                    : canAfford
                    ? 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
                onClick={() => canAfford && setSelectedGift(gift)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{gift.icon}</span>
                    <div>
                      <h3 className="font-semibold">{gift.name}</h3>
                      <p className="text-sm text-gray-600">{gift.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${canAfford ? 'text-blue-600' : 'text-gray-400'}`}>
                      {gift.price.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">T Coins</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Custom Message */}
        {selectedGift && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ข้อความ (ไม่บังคับ)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="เขียนข้อความให้กำลังใจ..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{message.length}/200</p>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isGiving}
          >
            ยกเลิก
          </button>
          <button
            onClick={handleGiveGift}
            disabled={!selectedGift || isGiving || totalTCoins < (selectedGift?.price || 0)}
            className="flex-1 py-2 px-4 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isGiving ? 'กำลังให้...' : `ให้${selectedGift?.name || 'ของขวัญ'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftModal;



